import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Row,
  Select,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import {
  VIOLATION_CONFIG,
  getViolationLabel,
  getSeverityTagColor,
} from "../../../config/violationConfig";

const STATUS_COLOR = { draft: "warning", closed: "success" };

/* ── stat card ─────────────────────────────────────────────────── */
const StatCard = ({ icon, label, value, color, sub }) => (
  <Card
    style={{ borderRadius: 14, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}
    styles={{ body: { padding: "18px 20px" } }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          width: 48, height: 48, borderRadius: 12,
          background: `${color}18`, display: "flex",
          alignItems: "center", justifyContent: "center",
          fontSize: 22, color, flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  </Card>
);

/* ── violations calendar ────────────────────────────────────────── */
const ViolationCalendar = ({ year, month, violations }) => {
  const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const violationMap = {};
  violations.forEach((v) => {
    const day = dayjs(v.date).date();
    if (!violationMap[day]) violationMap[day] = [];
    violationMap[day].push(v);
  });

  const firstDay  = dayjs(`${year}-${month}-01`).day();
  const offset    = firstDay === 0 ? 6 : firstDay - 1;
  const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
        {WEEK_DAYS.map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center", fontSize: 11, fontWeight: 700,
              color: d === "Sat" || d === "Sun" ? "#1890ff" : "#888",
              padding: "4px 0",
            }}
          >
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((dayNum, idx) => {
          if (!dayNum) return <div key={`e-${idx}`} />;
          const vList = violationMap[dayNum] || [];
          const hasViolation = vList.length > 0;

          return (
            <Tooltip
              key={dayNum}
              title={
                hasViolation
                  ? vList.map((v, i) => (
                      <div key={i} style={{ fontSize: 11 }}>
                        <strong>{getViolationLabel(v.rule)}</strong>: {v.detail}
                      </div>
                    ))
                  : null
              }
            >
              <div
                style={{
                  borderRadius: 8,
                  padding: "6px 4px",
                  border: hasViolation ? "1.5px solid #ffccc7" : "1.5px solid #f0f0f0",
                  background: hasViolation ? "#fff2f0" : "#fafafa",
                  minHeight: 48,
                  position: "relative",
                  cursor: hasViolation ? "pointer" : "default",
                }}
              >
                <div
                  style={{
                    fontSize: 13, fontWeight: 500, lineHeight: 1,
                    color: hasViolation ? "#cf1322" : "#bbb",
                  }}
                >
                  {dayNum}
                </div>
                {hasViolation && (
                  <Badge
                    count={vList.length}
                    size="small"
                    style={{
                      position: "absolute", top: 3, right: 3,
                      fontSize: 9, minWidth: 14, height: 14,
                      lineHeight: "14px", padding: "0 3px",
                    }}
                  />
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>
      {/* legend */}
      <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 11, color: "#888" }}>
        <span><span style={{ color: "#cf1322", marginRight: 4 }}>●</span>Has violations</span>
        <span><span style={{ color: "#d9d9d9", marginRight: 4 }}>●</span>No violations</span>
      </div>
    </div>
  );
};

/* ── violations table columns ────────────────────────────────────── */
const violationColumns = [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    align: "center",
    width: 120,
    sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    render: (d) => dayjs(d).format("DD MMM YYYY"),
  },
  {
    title: "Rule",
    dataIndex: "rule",
    key: "rule",
    align: "center",
    width: 220,
    filters: Object.entries(VIOLATION_CONFIG).map(([r, c]) => ({ text: c.label, value: r })),
    onFilter: (value, record) => record.rule === value,
    render: (r) => (
      <Tag color={getSeverityTagColor(VIOLATION_CONFIG[r]?.severity)} style={{ fontWeight: 600 }}>
        {getViolationLabel(r)}
      </Tag>
    ),
  },
  {
    title: "Detail",
    dataIndex: "detail",
    key: "detail",
    align: "left",
  },
];

/* ── main component ──────────────────────────────────────────────── */
const RestHoursRecordsCom = () => {
  const location = useLocation();
  const [loading, setLoading]       = useState(false);
  const [record, setRecord]         = useState(null);
  const [ships, setShips]           = useState([]);
  const [shipId, setShipId]         = useState(location.state?.ship_id || null);
  const [staffList, setStaffList]   = useState([]);
  const [staffId, setStaffId]       = useState(location.state?.staff_id || null);
  const [selectedMonth, setSelectedMonth] = useState(
    location.state?.year && location.state?.month
      ? dayjs(`${location.state.year}-${String(location.state.month).padStart(2, "0")}-01`)
      : dayjs()
  );

  const initialStaffId = location.state?.staff_id || null;
  const initialShipId = location.state?.ship_id || null;

  useEffect(() => {
    const FORM_DATA = new FormData();
    post(ApiURL.list_ships_view_com, FORM_DATA).then((res) => {
      if (res?.status === true) {
        const list = res.ships || [];
        setShips(list);
        const selectedShipId = initialShipId || list[0]?.id;
        if (selectedShipId) {
          setShipId(selectedShipId);
          fetchStaff(selectedShipId, initialStaffId);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStaff = (sid, preselectedStaffId = null) => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("ship_id", sid);
    post(ApiURL.list_register_staff, FORM_DATA).then((res) => {
      if (res?.status === true) {
        const list = res.staff || res.data || [];
        setStaffList(list);
        const first = preselectedStaffId || list[0]?.staff_id || list[0]?.id;
        if (first) {
          setStaffId(first);
          fetchRecord(selectedMonth, first, sid);
        }
      }
    });
  };

  const fetchRecord = (month = selectedMonth, sid = staffId, selectedShipId = shipId) => {
    if (!sid) return;
    setLoading(true);
    setRecord(null);
    const FORM_DATA = new FormData();
    FORM_DATA.append("staff_id", sid);
    if (selectedShipId) FORM_DATA.append("ship_id", selectedShipId);
    FORM_DATA.append("year", month.format("YYYY"));
    FORM_DATA.append("month", month.format("M"));
    post(ApiURL.view_rest_hours_record_com, FORM_DATA).then((res) => {
      setRecord(res?.status === true ? res.data || null : null);
      setLoading(false);
    });
  };

  const violations   = record?.violations || [];
  const ruleBreakdown = Object.entries(
    violations.reduce((acc, v) => {
      acc[v.rule] = (acc[v.rule] || 0) + 1;
      return acc;
    }, {})
  );

  return (
    <>
      {/* ── Filter bar ── */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "16px 20px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          marginBottom: 16,
          marginTop: 10,
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 160px", minWidth: 140 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: 500 }}>Ship</div>
          <Select
            placeholder="Select Ship"
            value={shipId}
            style={{ width: "100%" }}
            onChange={(val) => {
              setShipId(val);
              setStaffId(null);
              setRecord(null);
              fetchStaff(val);
            }}
          >
            {ships.map((s) => (
              <Select.Option key={s.id} value={s.id}>{s.ship_name}</Select.Option>
            ))}
          </Select>
        </div>

        <div style={{ flex: "1 1 160px", minWidth: 140 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: 500 }}>Crew Member</div>
          <Select
            placeholder="Select Staff"
            value={staffId}
            style={{ width: "100%" }}
            onChange={(val) => setStaffId(val)}
          >
            {staffList.map((s) => (
              <Select.Option key={s.staff_id || s.id} value={s.staff_id || s.id}>
                {s.staff_name || [s.first_name, s.last_name].filter(Boolean).join(" ") || "N/A"}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div style={{ flex: "1 1 160px", minWidth: 140 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: 500 }}>Month</div>
          <DatePicker
            picker="month"
            value={selectedMonth}
            format="MMMM YYYY"
            style={{ width: "100%" }}
            disabledDate={(c) => c && c > dayjs().endOf("month")}
            onChange={(val) => { if (val) setSelectedMonth(val); }}
          />
        </div>

        <div>
          <Button
            type="primary"
            loading={loading}
            style={{ height: 32 }}
            onClick={() => fetchRecord(selectedMonth, staffId)}
          >
            Search
          </Button>
        </div>
      </div>

      {/* ── Staff header card ── */}
      {record && (
        <Card
          style={{
            borderRadius: 14,
            border: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            marginBottom: 16,
            overflow: "hidden",
          }}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {/* Accent panel */}
            <div
              style={{
                background: record.is_compliant
                  ? "linear-gradient(135deg, #52c41a, #95de64)"
                  : "linear-gradient(135deg, #f5222d, #ff7875)",
                minWidth: 200,
                padding: "24px 20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 8,
                flex: "0 0 200px",
              }}
            >
              <div
                style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: "#ffffff33",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26, color: "#fff",
                }}
              >
                <UserOutlined />
              </div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, textAlign: "center" }}>
                {record.staff_name}
              </div>
              <div style={{ color: "#ffffffcc", fontSize: 12 }}>{record.role}</div>
              <div
                style={{
                  background: "#ffffff33", color: "#fff",
                  borderRadius: 20, padding: "2px 14px",
                  fontSize: 12, fontWeight: 600, marginTop: 4,
                }}
              >
                {record.is_compliant ? "✓ Compliant" : "✗ Non-Compliant"}
              </div>
            </div>

            {/* Details panel */}
            <div style={{ flex: 1, padding: "20px 24px" }}>
              <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                <Descriptions.Item label="Period">
                  <strong>{dayjs(`${record.year}-${record.month}-01`).format("MMMM YYYY")}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Record Status">
                  <Tag color={STATUS_COLOR[record.record_status] || "default"} style={{ textTransform: "capitalize" }}>
                    {record.record_status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Compliance">
                  {record.is_compliant ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>Compliant</Tag>
                  ) : (
                    <Tag color="error" icon={<CloseCircleOutlined />}>Non-Compliant</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Seafarer Signed">
                  {record.seafarer_signed_at
                    ? dayjs(record.seafarer_signed_at).format("DD MMM YYYY")
                    : <span style={{ color: "#aaa" }}>Not signed</span>}
                </Descriptions.Item>
                <Descriptions.Item label="Master Signed">
                  {record.master_signed_at
                    ? dayjs(record.master_signed_at).format("DD MMM YYYY")
                    : <span style={{ color: "#aaa" }}>Not signed</span>}
                </Descriptions.Item>
                <Descriptions.Item label="Closed At">
                  {record.closed_at
                    ? dayjs(record.closed_at).format("DD MMM YYYY")
                    : <span style={{ color: "#aaa" }}>—</span>}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </div>
        </Card>
      )}

      {/* ── Stat cards ── */}
      {record && (
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              icon={<ClockCircleOutlined />}
              label="Total Rest Hours"
              value={`${Number(record.total_rest_hours).toFixed(1)} h`}
              color="#52c41a"
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              icon={<ThunderboltOutlined />}
              label="Total Work Hours"
              value={`${Number(record.total_work_hours).toFixed(1)} h`}
              color="#1890ff"
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              icon={<WarningOutlined />}
              label="Total Violations"
              value={record.total_violations}
              color={record.total_violations === 0 ? "#52c41a" : "#f5222d"}
              sub={
                ruleBreakdown.length > 0
                  ? ruleBreakdown.map(([r, c]) => `${getViolationLabel(r)}: ${c}`).join(" · ")
                  : undefined
              }
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              icon={<FileTextOutlined />}
              label="Record Status"
              value={record.record_status?.charAt(0).toUpperCase() + record.record_status?.slice(1)}
              color={record.record_status === "closed" ? "#52c41a" : "#fa8c16"}
            />
          </Col>
        </Row>
      )}

      {/* Monthly violation banner */}
      {record?.total_violations > 0 && (
        <div
          style={{
            background: "#FFEBEE",
            border: "1px solid #ffccc7",
            borderRadius: 8,
            padding: "10px 16px",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <span style={{ color: "#D32F2F", fontWeight: 700 }}>
              {record.total_violations} violation(s) this month
            </span>
            <span style={{ color: "#888", fontSize: 12, marginLeft: 10 }}>
              Review the calendar and table below for details
            </span>
          </div>
          {record.total_violations > 0 && !record.is_compliant && (
            <Tag color="error" style={{ fontSize: 12, marginLeft: 8 }}>Non-Compliant</Tag>
          )}
        </div>
      )}

      {/* ── Calendar + Violations table ── */}
      {record && (
        <Row gutter={[16, 16]}>
          {/* Calendar */}
          <Col xs={24} md={10}>
            <Card
              title="Violation Calendar"
              style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}
              styles={{ body: { padding: 16 } }}
              extra={
                <Tag color="error">{violations.length} violation{violations.length !== 1 ? "s" : ""}</Tag>
              }
            >
              <ViolationCalendar
                year={record.year}
                month={record.month}
                violations={violations}
              />
            </Card>
          </Col>

          {/* Violations table */}
          <Col xs={24} md={14}>
            <Card
              title={
                <span>
                  <WarningOutlined style={{ color: "#f5222d", marginRight: 8 }} />
                  STCW Violations
                </span>
              }
              style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
              styles={{ body: { padding: "0 0 8px" } }}
              extra={
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {ruleBreakdown.map(([rule, count]) => (
                    <Tag key={rule} color={getSeverityTagColor(VIOLATION_CONFIG[rule]?.severity)}>
                      {getViolationLabel(rule)}: {count}
                    </Tag>
                  ))}
                </div>
              }
            >
              <Table
                size="small"
                bordered={false}
                columns={violationColumns}
                dataSource={violations.map((v, i) => ({ ...v, key: i }))}
                pagination={{ pageSize: 10, showTotal: (t) => `${t} violations` }}
                scroll={{ x: 500 }}
                locale={{ emptyText: "No violations — fully compliant" }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* ── Empty state ── */}
      {!record && !loading && (
        <Card
          style={{
            borderRadius: 14, textAlign: "center", marginTop: 16,
            border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            padding: "40px 0",
          }}
        >
          <FileTextOutlined style={{ fontSize: 40, color: "#d9d9d9", marginBottom: 12 }} />
          <div style={{ color: "#aaa", fontSize: 14 }}>
            Select a crew member and month, then click Search.
          </div>
        </Card>
      )}
    </>
  );
};

export default RestHoursRecordsCom;
